/* eslint-disable max-statements */
import { Button, Flex, Stack } from '@mantine/core';
import { cloneDeep, uniqBy } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import { Declaration } from '@e-origin/shared';

import { anomalyValueIcon, IconFactory, noSentAnomaliesIcon } from '@assets';
import { IconBox, Table, TableActions, TableColumnProps } from '@components';
import { retrieveAdditionalCodes } from '@hooks/batch';
import { useFinalizeFieldRules } from '../../policy';
import { CommodityCodeRow } from './commodityCodeRow';
import { EditCommodityCode } from './EditCommodityCode';

interface FinalizeCommodityCodesProps {
  declaration: Declaration;
  disabled: boolean;
}

export const FinalizeCommodityCodes: React.FC<FinalizeCommodityCodesProps> = ({ declaration, disabled }) => {
  const { form } = useFinalizeFieldRules();
  const [rows, setRows] = useState<CommodityCodeRow[]>([]);
  const [showModal, setShowModal] = useState(false);
  const selectedRow = useRef<CommodityCodeRow>(null);
  const [loadingNationalCodes, setLoadingNationalCodes] = useState(false);

  const toggleShowEditItemModal = () => {
    setShowModal(!showModal);
  };

  useEffect(() => {
    const tableData: CommodityCodeRow[] = cloneDeep(form.values.commodityCodes as CommodityCodeRow[]);
    setRows(tableData);
  }, []);

  const handleSavingItem = (itemData: CommodityCodeRow) => {
    const updatedRows = cloneDeep(rows).map((row) => {
      if (row.firstItemData.hsCode === itemData.firstItemData.hsCode) {
        return itemData;
      }
      return row;
    });
    setRows(updatedRows);
    form.setFieldValue('commodityCodes', updatedRows);
  };

  const populateDeclarationWithNationalCodes = () => {
    setLoadingNationalCodes(true);
    retrieveAdditionalCodes({ id: declaration.messageInfo.batch }).then((res) => {
      const updatedRows = rows.map<CommodityCodeRow>((row) => {
        const data = res.find((item) => item.firstItemData.hsCode === row.firstItemData.hsCode);

        if (data) {
          return {
            riskAnalysis: data.riskAnalysis,
            firstItemData: {
              nationalAdditionalCode: data.firstItemData.nationalAdditionalCode,
              nationalAdditionalCode2: data.firstItemData.nationalAdditionalCode2,
              nationalAdditionalCode3: data.firstItemData.nationalAdditionalCode3,
              taricAdditionalCode: data.firstItemData.taricAdditionalCode,
              taricAdditionalCode2: data.firstItemData.taricAdditionalCode2,
              hsCode: data.firstItemData.hsCode,
            },
            additionalReference: uniqBy(
              [
                ...data.riskAnalysis.additionalReference.map((sd) => ({
                  selected: true,
                  document: {
                    type: sd.type,
                    referenceNumber: sd.referenceNumber,
                    dateOfValidity: sd.dateOfValidity,
                    header: false,
                  },
                })),
                ...data.newYCodes.map((code) => ({
                  selected: false,
                  document: {
                    type: code,
                    referenceNumber: '.',
                    dateOfValidity: undefined,
                    header: false,
                  },
                })),
              ],
              (sd) => `${sd.document.type}#${sd.document.referenceNumber}`,
            ),
          };
        }

        return row;
      });
      setRows(updatedRows);
      form.setFieldValue('commodityCodes', updatedRows);
      setLoadingNationalCodes(false);
    });
  };

  const handleTableAction = (type: string, row: CommodityCodeRow) => {
    switch (type) {
      case 'view':
        selectedRow.current = row;
        toggleShowEditItemModal();
        break;
      default:
        break;
    }
  };

  const columns = useRef<TableColumnProps<CommodityCodeRow>[]>([
    {
      name: 'Status',
      sortable: false,
      cell: (row) => {
        return <IconBox icon={row.riskAnalysis?.hsCodeFound ? noSentAnomaliesIcon : anomalyValueIcon} alt={'anomalies-detected-value'} />;
      },
    },
    {
      name: 'HS Code',
      cell: (row, rowIdx) => <span data-pw={`cell-hs-code-${rowIdx}`}>{row.firstItemData.hsCode}</span>,
    },
    {
      name: 'Taric Additional Code 1',
      cell: (row, rowIdx) => <span data-pw={`cell-taric-code1-${rowIdx}`}>{row.firstItemData.taricAdditionalCode}</span>,
    },
    {
      name: 'Taric Additional Code 2',
      cell: (row) => row.firstItemData.taricAdditionalCode2,
    },
    {
      name: 'National Additional Code 1',
      cell: (row, rowIdx) => <span data-pw={`cell-national-code1-${rowIdx}`}>{row.firstItemData.nationalAdditionalCode}</span>,
    },
    {
      name: 'National Additional Code 2',
      cell: (row) => row.firstItemData.nationalAdditionalCode2,
    },
    {
      name: 'National Additional Code 3',
      cell: (row) => row.firstItemData.nationalAdditionalCode3,
    },
    {
      name: 'Actions',
      sticky: 'right',
      cell: (row: CommodityCodeRow, rowIdx: number) => (
        <TableActions data-pw={rowIdx} actions={[{ icon: IconFactory.view, text: 'View details', onClick: () => handleTableAction('view', row) }]} />
      ),
    },
  ]);

  return (
    <Stack style={{ flexShrink: 0 }}>
      <Flex justify={'end'} py={12}>
        {!disabled && (
          <Button data-pw="get-codes-btn" onClick={populateDeclarationWithNationalCodes} variant="outline" size="lg">
            Get Codes
          </Button>
        )}
      </Flex>
      <Table<CommodityCodeRow>
        data-pw="commodity-codes-table"
        columns={columns.current}
        dataSource={rows}
        defaultPerPage={5}
        isLoading={loadingNationalCodes}
        pagination
        verticalSpacing="xs"
      />
      {showModal && (
        <EditCommodityCode
          opened={showModal}
          onHide={toggleShowEditItemModal}
          commodityCode={selectedRow.current}
          onSave={handleSavingItem}
          disabled={disabled}
          declaration={declaration}
        />
      )}
    </Stack>
  );
};
